import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Stack,
  Divider,
  Container,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
     import { Link as RouterLink } from "react-router-dom";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import Navbar from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Helmet } from "react-helmet";
import { payWithPaystack } from "../utils/paystack";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import PageContainer from "../components/PageContainer";
export default function PricingPublic() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const plans = [
    { id: "monthly", name: "Monthly", duration: "1 Month", price: 1200 },
    { id: "quarterly", name: "Quarterly", duration: "3 Months", price: 3000 },
    { id: "half", name: "Half-Year", duration: "6 Months", price: 5500, popular: true },
    { id: "yearly", name: "Yearly", duration: "12 Months", price: 10000 },
  ];

  return (
    <PageContainer>

      {/* ================= SEO ================= */}
      <Helmet>
        <title>Pricing & Subscription Plans | CBT Nova</title>
        <meta
          name="description"
          content="Affordable CBT practice subscriptions for JAMB, WAEC and NECO. Unlimited practice, timed exams, analytics, and instant explanations."
        />

        {/* Schema.org Pricing */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "CBT Nova Subscription",
            description:
              "Online CBT exam preparation platform for WAEC, JAMB, and NECO.",
            offers: plans.map((p) => ({
              "@type": "Offer",
              priceCurrency: "NGN",
              price: p.price,
              availability: "https://schema.org/InStock",
            })),
          })}
        </script>
      </Helmet>

      <Navbar />

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* ================= HERO ================= */}
        <Box textAlign="center" sx={{ mb: 6 }}>
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Simple, Affordable Pricing for Exam Success
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 720, mx: "auto" }}>
            Prepare confidently for WAEC, JAMB, and NECO using realistic CBT
            practice, instant feedback, and detailed performance analytics.
          </Typography>
        </Box>

        {/* ================= PRICING CARDS ================= */}
        <Grid container spacing={3} sx={{ mb: 7 }}>
          {plans.map((plan) => (
            <Grid item xs={12} sm={6} md={3} key={plan.name}>
              <Paper
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: 3,
                  border: plan.popular
                    ? "2px solid #1976d2"
                    : "1px solid #eee",
                }}
              >
                {plan.popular && (
                  <Chip label="Most Popular" color="primary" size="small" />
                )}

                <Typography fontWeight={700} sx={{ mt: 1 }}>
                  {plan.name}
                </Typography>
                <Typography color="text.secondary">
                  Full access for {plan.duration}
                </Typography>

                <Typography variant="h4" fontWeight={800} sx={{ my: 2 }}>
                  ₦{plan.price.toLocaleString()}
                </Typography>

                <Button
                  fullWidth
                  variant={plan.popular ? "contained" : "outlined"}
                  startIcon={<CreditCardIcon />}
                >
                  Get Started
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ================= COMPARISON ================= */}
        <Paper sx={{ p: 4, mb: 7, overflowX: "auto" }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Free vs Paid Comparison
          </Typography>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Feature</TableCell>
                <TableCell align="center">Free</TableCell>
                <TableCell align="center">Paid</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                ["Real CBT Practice", true, true],
				 ["Multi-Subject", false, true],
				  ["Syncronize Questions", true, true],
				  ["Question Update", true, true],
                ["Syncronized Timed Exams", false, true],
                ["Result and Analyses", false, true],
                ["Detailed Explanations", false, true],
                ["Performance Analytics", false, true],
                ["Leaderboard Access", false, true],
              ].map(([label, free, paid]) => (
                <TableRow key={label}>
                  <TableCell>{label}</TableCell>
                  <TableCell align="center">
                    {free ? <CheckCircleIcon color="success" /> : <CloseIcon color="error" />}
                  </TableCell>
                  <TableCell align="center">
                    <CheckCircleIcon color="success" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        {/* ================= TESTIMONIALS ================= */}
        <Paper sx={{ p: 4, mb: 7 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
            What Students Are Saying
          </Typography>

          <Grid container spacing={3}>
            {[
              { name: "Aisha (JAMB)", text: "The CBT simulations felt like the real exam. Very helpful!" },
              { name: "Abubakar (WAEC)", text: "Instant explanations made studying much easier." },
              { name: "Odunayo (NECO)", text: "I could track my progress and improve weekly." },
            ].map((t) => (
              <Grid item xs={12} md={4} key={t.name}>
                <Paper sx={{ p: 3, height: "100%" }}>
                  <Typography>"{t.text}"</Typography>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                    — {t.name}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* ================= CTA ================= */}
        <Paper
          sx={{
            p: 5,
            mb: 7,
            textAlign: "center",
            background: "linear-gradient(135deg, #1976d2, #0d47a1)",
            color: "#fff",
          }}
        >
          <Typography variant="h5" fontWeight={800}>
            Ready to Ace Your Exams?
          </Typography>
          <Typography sx={{ mt: 1, mb: 3 }}>
            Join thousands of students preparing smarter with CBT Nova.
          </Typography>
   

<Button
  variant="contained"
  color="secondary"
  component={RouterLink}
  to="/register"
>
  Start Practicing Now
</Button>
        </Paper>

        {/* ================= TERMS (VERBATIM) ================= */}
        <Paper sx={{ p: 4 }}>
          <Typography variant="h6" fontWeight={700}>
            Subscription Terms
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Typography sx={{ lineHeight: 1.85 }}>
            CBT Nova offers paid subscription plans that grant access to premium
            features, including full practice exams, analytics, and advanced
            learning tools. By subscribing, you acknowledge and agree that:
            <br /><br />
            • Only one active subscription is permitted per user at any time <br />
            • Subscriptions activate immediately after successful payment confirmation <br />
            • Access is linked strictly to the subscribed account and may not be shared,
            transferred, or resold <br />
            • Subscription access expires automatically at the end of the plan duration
            <br /><br />
            Users may subscribe to a new plan at any time. When a new subscription
            is activated:
            <br /><br />
            • Any existing active subscription is automatically ended <br />
            • The new plan becomes effective immediately <br />
            • Remaining time on a previous plan does not carry over
            <br /><br />
            All payments made on CBT Nova are final and non-refundable, except
            where required by applicable law.
            <br /><br />
            These terms form part of CBT Nova’s full{" "}
            <strong>Terms & Conditions</strong>, available on the Terms page.
          </Typography>
        </Paper>
      </Container>

      <Footer />
    </PageContainer>
  );
}
